// Copyright (c) Microsoft. All rights reserved.

import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import React from 'react';
import { GetResponseOptions, useChat } from '../../libs/hooks/useChat';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { FeatureKeys, Features } from '../../redux/features/app/AppState';
import { Breakpoints, SharedStyles, customTokens } from '../../styles';
import { ChatInput } from './ChatInput';
import { ChatHistory } from './chat-history/ChatHistory';
import { ConversationStyle } from './ConversationStyle';
import { TemperatureType } from '../../libs/models/TemperatureType';
import { ChatState } from '../../redux/features/conversations/ChatState';
import { editConversationTemperature } from '../../redux/features/conversations/conversationsSlice';

const useClasses = makeStyles({
    root: {
        ...shorthands.overflow('hidden'),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        backgroundColor: '#fff',
    },
    scroll: {
        ...shorthands.margin(tokens.spacingVerticalXS),
        ...SharedStyles.scroll,
    },
    history: {
        ...shorthands.padding(tokens.spacingVerticalM),
        // marginLeft: '40px',
        // paddingRight: '40px',
        display: 'flex',
        justifyContent: 'center',
        ...Breakpoints.small({
            marginLeft: '0px!important',
            paddingRight: '0px',
        }),
    },
    input: {
        ...shorthands.padding(tokens.spacingVerticalXS),
    },
});

export const ChatRoom: React.FC = () => {
    const classes = useClasses();
    const chat = useChat();
    const dispatch = useAppDispatch();

    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const messages = conversations[selectedId].messages;

    const scrollViewTargetRef = React.useRef<HTMLDivElement>(null);
    const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);

    const [isDraggingOver, setIsDraggingOver] = React.useState(false);
    const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDraggingOver(true);
    };
    const onDragLeave = (e: React.DragEvent<HTMLDivElement | HTMLTextAreaElement>) => {
        e.preventDefault();
        setIsDraggingOver(false);
    };

    React.useEffect(() => {
        if (!shouldAutoScroll) return;
        scrollViewTargetRef.current?.scrollTo(0, scrollViewTargetRef.current.scrollHeight);
    }, [messages, shouldAutoScroll]);

    React.useEffect(() => {
        const onScroll = () => {
            if (!scrollViewTargetRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = scrollViewTargetRef.current;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
            setShouldAutoScroll(isAtBottom);
        };

        if (!scrollViewTargetRef.current) return;

        const currentScrollViewTarget = scrollViewTargetRef.current;

        currentScrollViewTarget.addEventListener('scroll', onScroll);
        return () => {
            currentScrollViewTarget.removeEventListener('scroll', onScroll);
        };
    }, []);

    const handleSubmit = async (options: GetResponseOptions) => {
        await chat.getResponse(options);
        setShouldAutoScroll(true);
    };

    const onUpdateTemperature = async (temperature: TemperatureType) => {
        const chatState: ChatState = conversations[selectedId];
        dispatch(editConversationTemperature({ id: selectedId, temperature: temperature }));
        await chat.editChat(
            selectedId,
            chatState.title,
            chatState.systemDescription,
            chatState.memoryBalance,
            temperature,
        );
    };

    const getTemperatureColor = () => {
        const temperature = conversations[selectedId].temperature;
        switch (temperature) {
            case TemperatureType.Balanced:
                return customTokens.balanced;
            case TemperatureType.Creative:
                return customTokens.creative;
            case TemperatureType.Precise:
                return customTokens.precise;
            default:
                return customTokens.balanced;
        }
    };

    if (conversations[selectedId].hidden) {
        return (
            <div className={classes.root}>
                <div className={classes.scroll}>
                    <div className={classes.history}>
                        <h3>
                            This conversation is not visible in the app because{' '}
                            {Features[FeatureKeys.MultiUserChat].label} is disabled. Please enable the feature in the
                            settings to view the conversation, select a different one, or create a new conversation.
                        </h3>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.root} onDragEnter={onDragEnter} onDragOver={onDragEnter} onDragLeave={onDragLeave}>
            {messages.length >= 1 && <ConversationStyle onUpdateTemperature={onUpdateTemperature} />}
            <div ref={scrollViewTargetRef} className={classes.scroll}>
                <div className={classes.history}>
                    <ChatHistory messages={messages} />
                </div>
            </div>
            <div className={classes.input}>
                <ChatInput
                    isDraggingOver={isDraggingOver}
                    onDragLeave={onDragLeave}
                    onSubmit={handleSubmit}
                    bgColor={getTemperatureColor()}
                />
            </div>
        </div>
    );
};
